import { type ReactElement } from 'react';
import { redirect, useLoaderData } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { MoneyDocumentSchema, type MoneyDocument } from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { DocumentView } from '../features/billing/documents/DocumentView.js';
import { type DocumentLocale } from '../features/billing/documents/rows.js';
import { ApiError } from '../shared/api.js';
import { routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';
import { Component as NotFound } from './not-found.js';

/**
 * `/:lang/account/documents/:id` — one provisional receipt or statement, and its PDF. Anybody's but
 * the reader's own is the API's `404`, shown as `not-found`, as an unknown id is. `W5-T09` §4.9,
 * AC 36; owned by `agent-money`.
 */
type DocumentData =
  { view: 'notFound' } | { view: 'document'; locale: DocumentLocale; doc: MoneyDocument };

export async function loader({ params, context }: LoaderFunctionArgs): Promise<DocumentData> {
  const locale = localeOf(params) === 'en' ? 'en' : 'es';
  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const id = params['id'] ?? '';
  // A malformed id is the API's 404 too; there is no request worth sending for it.
  if (!MoneyDocumentSchema.options[0].shape.id.safeParse(id).success) return { view: 'notFound' };

  const { api } = context.get(routeContext);
  try {
    return { view: 'document', locale, doc: await api.getDocument(id) };
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return { view: 'notFound' };
    if (error instanceof ApiError && error.status === 401) throw redirect(`/${locale}/login`);
    throw error;
  }
}

export function Component(): ReactElement {
  const data = useLoaderData<DocumentData>();
  if (data.view === 'notFound') return <NotFound />;
  return <DocumentView doc={data.doc} locale={data.locale} />;
}
